import { useState, useEffect } from 'react';
import { useAuthStore } from '../../store/authStore';
import { apiHelpers } from '../../services/api';
import toast from 'react-hot-toast';
import {
  CodeBracketIcon,
  ClipboardDocumentIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  KeyIcon,
  EyeIcon,
  EyeSlashIcon,
  BeakerIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

// Code snippet templates
const getHTMLSnippet = (apiKey, filters, sdkUrl, apiUrl, displayMode = 'floating') => {
  const filterLines = filters.map(f => 
    `        ${f.fieldName}: 'YOUR_${f.fieldName.toUpperCase()}', // ${f.required ? 'Required' : 'Optional'}: ${f.displayName}`
  ).join('\n');
  
  if (displayMode === 'embedded') {
    return `<!-- NeoShift AI SDK Integration (Embedded Mode) -->
<!-- Container where the chat will be embedded -->
<div id="neoshift-chat-container" style="width: 100%; height: 500px;"></div>

<script>
  // Load SDK dynamically to ensure proper initialization
  (function() {
    var script = document.createElement('script');
    script.src = '${sdkUrl || 'https://cdn.neoshift.ai/sdk/v1/widget.js'}';
    script.onload = function() {
      if (window.NeoShiftAI) {
        window.NeoShiftAI.init({
          apiKey: '${apiKey || 'YOUR_API_KEY'}',
          apiUrl: '${apiUrl || 'https://api.neoshift.ai/api'}',
          
          // Embedded mode - chat is always visible
          displayMode: 'embedded',
          container: 'neoshift-chat-container', // ID of the container element
          height: '500px', // Optional: customize height
          width: '100%',   // Optional: customize width
          
          // Filter values - replace with actual values from your system
          filters: {
${filterLines || '            // No filters configured'}
          },
          
          // Callback when credits are used (for billing your users)
          onCreditsUsed: function(creditsUsed, creditBalance) {
            console.log('Credits used:', creditsUsed, 'Remaining:', creditBalance);
          },
          
          // Optional: Handle errors
          onError: function(error) {
            console.error('SDK Error:', error);
          }
        });
      }
    };
    script.onerror = function() {
      console.error('Failed to load NeoShift AI SDK');
    };
    document.head.appendChild(script);
  })();
</script>`;
  }
  
  return `<!-- NeoShift AI SDK Integration (Floating Button) -->
<script>
  // Load SDK dynamically to ensure proper initialization
  (function() {
    var script = document.createElement('script');
    script.src = '${sdkUrl || 'https://cdn.neoshift.ai/sdk/v1/widget.js'}';
    script.onload = function() {
      if (window.NeoShiftAI) {
        window.NeoShiftAI.init({
          apiKey: '${apiKey || 'YOUR_API_KEY'}',
          apiUrl: '${apiUrl || 'https://api.neoshift.ai/api'}',
          
          // Filter values - replace with actual values from your system
          filters: {
${filterLines || '            // No filters configured'}
          },
          
          // Callback when credits are used (for billing your users)
          onCreditsUsed: function(creditsUsed, creditBalance) {
            console.log('Credits used:', creditsUsed, 'Remaining:', creditBalance);
            // Track usage for your own billing system
            // trackUserUsage(currentUser.id, creditsUsed);
          },
          
          // Optional: Handle errors
          onError: function(error) {
            console.error('SDK Error:', error);
          }
        });
      }
    };
    script.onerror = function() {
      console.error('Failed to load NeoShift AI SDK');
    };
    document.head.appendChild(script);
  })();
</script>`;
};

const getReactSnippet = (apiKey, filters, sdkUrl, apiUrl, displayMode = 'floating') => {
  const filterLines = filters.map(f => 
    `          ${f.fieldName}: company.${f.fieldName}, // ${f.required ? 'Required' : 'Optional'}`
  ).join('\n');
  
  if (displayMode === 'embedded') {
    return `import { useEffect, useRef } from 'react';

function AIAssistantChat({ company, height = '500px', width = '100%' }) {
  const containerRef = useRef(null);
  const scriptRef = useRef(null);

  useEffect(() => {
    // Load SDK script dynamically
    const script = document.createElement('script');
    script.src = '${sdkUrl || 'https://cdn.neoshift.ai/sdk/v1/widget.js'}';
    scriptRef.current = script;
    
    script.onload = () => {
      if (window.NeoShiftAI && containerRef.current) {
        window.NeoShiftAI.init({
          apiKey: process.env.REACT_APP_NEOSHIFT_API_KEY || '${apiKey || 'YOUR_API_KEY'}',
          apiUrl: '${apiUrl || 'https://api.neoshift.ai/api'}',
          displayMode: 'embedded',
          container: containerRef.current, // Pass the DOM element directly
          height,
          width,
          filters: {
${filterLines || '            // No filters configured'}
          },
          onCreditsUsed: (creditsUsed, balance) => {
            console.log('Credits used:', creditsUsed, 'Remaining:', balance);
          },
          onError: (error) => {
            console.error('SDK Error:', error);
          }
        });
      }
    };
    
    script.onerror = () => {
      console.error('Failed to load NeoShift AI SDK');
    };
    
    document.head.appendChild(script);

    return () => {
      if (window.NeoShiftAI) {
        window.NeoShiftAI.destroy();
      }
      if (scriptRef.current?.parentNode) {
        scriptRef.current.parentNode.removeChild(scriptRef.current);
      }
    };
  }, [company, height, width]);

  return <div ref={containerRef} style={{ width, height }} />;
}

export default AIAssistantChat;

// Usage example:
// <AIAssistantChat company={currentCompany} height="600px" />`;
  }
  
  return `import { useEffect, useRef } from 'react';

function AIAssistantWidget({ company }) {
  const scriptRef = useRef(null);

  useEffect(() => {
    // Load SDK script dynamically
    const script = document.createElement('script');
    script.src = '${sdkUrl || 'https://cdn.neoshift.ai/sdk/v1/widget.js'}';
    scriptRef.current = script;
    
    script.onload = () => {
      if (window.NeoShiftAI) {
        window.NeoShiftAI.init({
          apiKey: process.env.REACT_APP_NEOSHIFT_API_KEY || '${apiKey || 'YOUR_API_KEY'}',
          apiUrl: '${apiUrl || 'https://api.neoshift.ai/api'}',
          filters: {
${filterLines || '            // No filters configured'}
          },
          onCreditsUsed: (creditsUsed, balance) => {
            console.log('Credits used:', creditsUsed, 'Remaining:', balance);
            // Record usage for billing
            // billingService.recordUsage(company.id, creditsUsed);
          },
          onError: (error) => {
            console.error('SDK Error:', error);
          }
        });
      }
    };
    
    script.onerror = () => {
      console.error('Failed to load NeoShift AI SDK');
    };
    
    document.head.appendChild(script);

    // Cleanup on unmount
    return () => {
      if (window.NeoShiftAI) {
        window.NeoShiftAI.destroy();
      }
      if (scriptRef.current && scriptRef.current.parentNode) {
        scriptRef.current.parentNode.removeChild(scriptRef.current);
      }
    };
  }, [company]);

  return null; // Widget renders itself as a floating button
}

export default AIAssistantWidget;`;
};

const getVueSnippet = (apiKey, filters, sdkUrl, apiUrl, displayMode = 'floating') => {
  const filterLines = filters.map(f => 
    `    ${f.fieldName}: props.company.${f.fieldName}, // ${f.required ? 'Required' : 'Optional'}`
  ).join('\n');
  
  if (displayMode === 'embedded') {
    return `<template>
  <!-- Container for embedded chat -->
  <div ref="chatContainer" :style="{ width: width, height: height }"></div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref, computed } from 'vue';

const props = defineProps({
  company: Object,
  height: { type: String, default: '500px' },
  width: { type: String, default: '100%' }
});

const chatContainer = ref(null);
const scriptElement = ref(null);

const filters = computed(() => ({
${filterLines || '  // No filters configured'}
}));

onMounted(() => {
  const script = document.createElement('script');
  script.src = '${sdkUrl || 'https://cdn.neoshift.ai/sdk/v1/widget.js'}';
  scriptElement.value = script;
  
  script.onload = () => {
    if (window.NeoShiftAI && chatContainer.value) {
      window.NeoShiftAI.init({
        apiKey: import.meta.env.VITE_NEOSHIFT_API_KEY || '${apiKey || 'YOUR_API_KEY'}',
        apiUrl: '${apiUrl || 'https://api.neoshift.ai/api'}',
        displayMode: 'embedded',
        container: chatContainer.value,
        height: props.height,
        width: props.width,
        filters: filters.value,
        onCreditsUsed: (creditsUsed, creditBalance) => {
          console.log('Credits used:', creditsUsed, 'Remaining:', creditBalance);
        },
        onError: (error) => {
          console.error('SDK Error:', error);
        }
      });
    }
  };
  
  script.onerror = () => {
    console.error('Failed to load NeoShift AI SDK');
  };
  
  document.head.appendChild(script);
});

onBeforeUnmount(() => {
  if (window.NeoShiftAI) {
    window.NeoShiftAI.destroy();
  }
  if (scriptElement.value?.parentNode) {
    scriptElement.value.parentNode.removeChild(scriptElement.value);
  }
});
</script>

<!-- Usage: <AIAssistantChat :company="company" height="600px" /> -->`;
  }
  
  return `<template>
  <!-- Widget renders itself as a floating button, no container needed -->
  <div></div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref, computed } from 'vue';

const props = defineProps(['company']);
const scriptElement = ref(null);

const filters = computed(() => ({
${filterLines || '  // No filters configured'}
}));

onMounted(() => {
  // Load SDK script dynamically
  const script = document.createElement('script');
  script.src = '${sdkUrl || 'https://cdn.neoshift.ai/sdk/v1/widget.js'}';
  scriptElement.value = script;
  
  script.onload = () => {
    if (window.NeoShiftAI) {
      window.NeoShiftAI.init({
        apiKey: import.meta.env.VITE_NEOSHIFT_API_KEY || '${apiKey || 'YOUR_API_KEY'}',
        apiUrl: '${apiUrl || 'https://api.neoshift.ai/api'}',
        filters: filters.value,
        onCreditsUsed: (creditsUsed, creditBalance) => {
          console.log('Credits used:', creditsUsed, 'Remaining:', creditBalance);
          // Bill customer or track usage
        },
        onError: (error) => {
          console.error('SDK Error:', error);
        }
      });
    }
  };
  
  script.onerror = () => {
    console.error('Failed to load NeoShift AI SDK');
  };
  
  document.head.appendChild(script);
});

onBeforeUnmount(() => {
  if (window.NeoShiftAI) {
    window.NeoShiftAI.destroy();
  }
  if (scriptElement.value && scriptElement.value.parentNode) {
    scriptElement.value.parentNode.removeChild(scriptElement.value);
  }
});
</script>`;
};

export default function SDKIntegration() {
  const { user, getCurrentCompanyId, getCurrentCompany, isSuperUser } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [sdkConfig, setSdkConfig] = useState(null);
  const [sdkInfo, setSdkInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('html');
  const [testFilters, setTestFilters] = useState({});
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  
  const companyId = getCurrentCompanyId();
  const company = getCurrentCompany();
  
  // Check if user has admin access
  const hasAdminAccess = user && ['SUPER_ADMIN', 'MANAGING_ADMIN', 'ADMIN'].includes(user.role);
  
  useEffect(() => {
    if (companyId) {
      loadSDKConfig();
      loadSDKInfo();
    }
  }, [companyId]);
  
  const loadSDKConfig = async () => {
    try {
      setLoading(true);
      const response = await apiHelpers.getSDKConfig(companyId);
      setSdkConfig(response.data);
      
      // Initialize test filters with empty values
      const initialFilters = {};
      response.data.allowedFilters?.forEach(f => {
        initialFilters[f.fieldName] = '';
      });
      setTestFilters(initialFilters);
    } catch (error) {
      console.error('Failed to load SDK config:', error);
      if (error.response?.status !== 404) {
        toast.error('Failed to load SDK configuration');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const loadSDKInfo = async () => {
    try {
      const response = await apiHelpers.getSDKInfo();
      setSdkInfo(response.data);
    } catch (error) {
      console.error('Failed to load SDK info:', error);
      // Non-critical, continue anyway
    }
  };
  
  const handleTest = async () => {
    setTesting(true);
    try {
      const response = await apiHelpers.testSDKConfig(companyId, { filters: testFilters });
      setTestResult(response.data);
      toast.success('SDK configuration is valid!');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Test failed');
      setTestResult({ error: error.response?.data?.error || 'Test failed' });
    } finally {
      setTesting(false);
    }
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };
  
  // Get masked API key for display
  const getMaskedApiKey = () => {
    if (!sdkConfig?.hasApiKey) return 'No API key generated';
    return `nsk_${'•'.repeat(20)}${sdkConfig.apiKeyLast4 || '****'}`;
  };
  
  // Check if company is enterprise
  const isEnterprise = company?.billingPlan === 'enterprise';
  
  if (!companyId) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-display font-bold text-surface-900 dark:text-white mb-2">
            No Company Selected
          </h2>
          <p className="text-surface-500 dark:text-surface-400">
            Please select a company to view SDK integration details.
          </p>
        </div>
      </div>
    );
  }
  
  if (!hasAdminAccess) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <XCircleIcon className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-display font-bold text-surface-900 dark:text-white mb-2">
            Access Denied
          </h2>
          <p className="text-surface-500 dark:text-surface-400">
            You need Admin access to view SDK integration details.
          </p>
        </div>
      </div>
    );
  }
  
  if (!isEnterprise) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <CodeBracketIcon className="w-12 h-12 text-surface-400 mx-auto mb-4" />
          <h2 className="text-xl font-display font-bold text-surface-900 dark:text-white mb-2">
            Enterprise Feature
          </h2>
          <p className="text-surface-500 dark:text-surface-400 max-w-md">
            The SDK integration is only available for Enterprise plan companies. 
            Contact sales to upgrade your plan.
          </p>
        </div>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!sdkConfig?.enabled) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <CodeBracketIcon className="w-12 h-12 text-surface-400 mx-auto mb-4" />
          <h2 className="text-xl font-display font-bold text-surface-900 dark:text-white mb-2">
            SDK Not Enabled
          </h2>
          <p className="text-surface-500 dark:text-surface-400 max-w-md mb-4">
            The SDK has not been enabled for this company yet. 
            Please contact your administrator to enable SDK access.
          </p>
          {isSuperUser() && (
            <p className="text-xs text-surface-400">
              Admins: Enable SDK in the Companies → View Details → SDK tab
            </p>
          )}
        </div>
      </div>
    );
  }
  
  const filters = sdkConfig.allowedFilters || [];
  
  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-surface-900 dark:text-white">
            SDK Integration
          </h1>
          <p className="text-surface-500 dark:text-surface-400 mt-1">
            Integrate the AI Assistant into your application
          </p>
        </div>
        <button onClick={loadSDKConfig} className="btn-secondary btn-sm">
          <ArrowPathIcon className="w-4 h-4 mr-1" />
          Refresh
        </button>
      </div>
      
      {/* SDK Status Banner */}
      <div className="card bg-gradient-to-r from-primary-500/10 to-accent-500/10 border-primary-500/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
              <CheckCircleIcon className="w-6 h-6 text-primary-500" />
            </div>
            <div>
              <h3 className="font-medium text-surface-900 dark:text-white">SDK Enabled</h3>
              <p className="text-sm text-surface-500 dark:text-surface-400">
                Your API key is ready for integration
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <code className="px-3 py-1.5 bg-surface-100 dark:bg-surface-800 rounded text-sm font-mono">
              {showApiKey ? getMaskedApiKey() : '••••••••••••••••'}
            </code>
            <button
              onClick={() => setShowApiKey(!showApiKey)}
              className="p-2 text-surface-400 hover:text-surface-600 dark:hover:text-surface-300"
            >
              {showApiKey ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
            </button>
          </div>
        </div>
        
        {/* Usage Stats */}
        <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-surface-200 dark:border-surface-700">
          <div className="text-center">
            <p className="text-2xl font-bold text-surface-900 dark:text-white">
              {sdkConfig.totalRequests?.toLocaleString() || 0}
            </p>
            <p className="text-xs text-surface-500">Total Requests</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-surface-900 dark:text-white">
              {sdkConfig.totalCreditsUsed?.toLocaleString() || 0}
            </p>
            <p className="text-xs text-surface-500">Credits Used</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-surface-900 dark:text-white">
              {sdkConfig.lastRequestAt ? new Date(sdkConfig.lastRequestAt).toLocaleDateString() : '-'}
            </p>
            <p className="text-xs text-surface-500">Last Request</p>
          </div>
        </div>
      </div>
      
      {/* Filter Fields Reference */}
      {filters.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-display font-semibold text-surface-900 dark:text-white mb-4">
            Required Filter Fields
          </h3>
          <p className="text-sm text-surface-500 dark:text-surface-400 mb-4">
            Your integration must provide these filter values to properly scope data access.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-surface-200 dark:border-surface-700">
                  <th className="text-left py-2 px-3 font-medium text-surface-600 dark:text-surface-300">Field Name</th>
                  <th className="text-left py-2 px-3 font-medium text-surface-600 dark:text-surface-300">Display Name</th>
                  <th className="text-left py-2 px-3 font-medium text-surface-600 dark:text-surface-300">Type</th>
                  <th className="text-left py-2 px-3 font-medium text-surface-600 dark:text-surface-300">Required</th>
                  <th className="text-left py-2 px-3 font-medium text-surface-600 dark:text-surface-300">Description</th>
                </tr>
              </thead>
              <tbody>
                {filters.map((filter, index) => (
                  <tr key={index} className="border-b border-surface-100 dark:border-surface-800">
                    <td className="py-2 px-3">
                      <code className="text-primary-600 dark:text-primary-400">{filter.fieldName}</code>
                    </td>
                    <td className="py-2 px-3 text-surface-700 dark:text-surface-300">{filter.displayName}</td>
                    <td className="py-2 px-3 text-surface-500">{filter.dataType}</td>
                    <td className="py-2 px-3">
                      {filter.required ? (
                        <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-2 py-0.5 rounded">
                          Required
                        </span>
                      ) : (
                        <span className="text-xs bg-surface-100 dark:bg-surface-800 text-surface-500 px-2 py-0.5 rounded">
                          Optional
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-surface-500 text-xs max-w-xs truncate">
                      {filter.description || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Custom Prompt Preview */}
      {sdkConfig.customPrompt && (
        <div className="card">
          <h3 className="text-lg font-display font-semibold text-surface-900 dark:text-white mb-4">
            Custom Prompt Configuration
          </h3>
          <p className="text-sm text-surface-500 dark:text-surface-400 mb-4">
            This prompt is sent to the AI to configure its behavior. Variables like <code className="text-primary-500">{`{{fieldName}}`}</code> are replaced with your filter values.
          </p>
          <pre className="bg-surface-100 dark:bg-surface-800 rounded-lg p-4 text-sm font-mono text-surface-700 dark:text-surface-300 whitespace-pre-wrap overflow-x-auto">
            {sdkConfig.customPrompt}
          </pre>
        </div>
      )}
      
      {/* SDK URLs Info */}
      {sdkInfo && (
        <div className="card bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
          <h3 className="text-lg font-display font-semibold text-surface-900 dark:text-white mb-4">
            SDK Widget URL
          </h3>
          <div className="space-y-2">
            <div>
              <label className="text-xs text-surface-500 dark:text-surface-400">Production (versioned, recommended)</label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 text-sm bg-white dark:bg-surface-800 px-3 py-2 rounded font-mono text-surface-700 dark:text-surface-300 break-all">
                  {sdkInfo.urls.widget}
                </code>
                <button
                  onClick={() => copyToClipboard(sdkInfo.urls.widget)}
                  className="p-2 text-surface-400 hover:text-surface-600 dark:hover:text-surface-300"
                >
                  <ClipboardDocumentIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
            {sdkInfo.urls.widgetLatest && (
              <div>
                <label className="text-xs text-surface-500 dark:text-surface-400">Latest (auto-updates)</label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 text-sm bg-white dark:bg-surface-800 px-3 py-2 rounded font-mono text-surface-700 dark:text-surface-300 break-all">
                    {sdkInfo.urls.widgetLatest}
                  </code>
                  <button
                    onClick={() => copyToClipboard(sdkInfo.urls.widgetLatest)}
                    className="p-2 text-surface-400 hover:text-surface-600 dark:hover:text-surface-300"
                  >
                    <ClipboardDocumentIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Display Mode Info */}
      <div className={`card ${sdkConfig.widgetConfig?.displayMode === 'embedded' ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800' : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'}`}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${sdkConfig.widgetConfig?.displayMode === 'embedded' ? 'bg-purple-500/20' : 'bg-blue-500/20'}`}>
            {sdkConfig.widgetConfig?.displayMode === 'embedded' ? (
              <svg className="w-5 h-5 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            )}
          </div>
          <div>
            <h3 className="font-medium text-surface-900 dark:text-white">
              Display Mode: {sdkConfig.widgetConfig?.displayMode === 'embedded' ? 'Embedded' : 'Floating Button'}
            </h3>
            <p className="text-sm text-surface-500 dark:text-surface-400">
              {sdkConfig.widgetConfig?.displayMode === 'embedded' 
                ? 'The chat interface will be embedded directly into a container element on your page (always visible).'
                : 'A floating chat button will appear in the corner. Users click to open the chat window.'}
            </p>
          </div>
        </div>
      </div>

      {/* Integration Code Tabs */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-display font-semibold text-surface-900 dark:text-white">
            Integration Code
          </h3>
          <div className="flex gap-1 bg-surface-100 dark:bg-surface-800 rounded-lg p-1">
            {['html', 'react', 'vue'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-1.5 text-sm font-medium rounded transition-colors ${
                  activeTab === tab
                    ? 'bg-white dark:bg-surface-700 text-surface-900 dark:text-white shadow-sm'
                    : 'text-surface-500 hover:text-surface-700 dark:hover:text-surface-300'
                }`}
              >
                {tab.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        
        <div className="relative">
          <button
            onClick={() => {
              const snippets = { html: getHTMLSnippet, react: getReactSnippet, vue: getVueSnippet };
              const sdkUrl = sdkInfo?.urls?.widget;
              const apiUrl = sdkInfo?.urls?.apiUrl;
              const displayMode = sdkConfig.widgetConfig?.displayMode || 'floating';
              copyToClipboard(snippets[activeTab](getMaskedApiKey(), filters, sdkUrl, apiUrl, displayMode));
            }}
            className="absolute top-2 right-2 p-2 bg-surface-200 dark:bg-surface-700 rounded hover:bg-surface-300 dark:hover:bg-surface-600 transition-colors"
            title="Copy code"
          >
            <ClipboardDocumentIcon className="w-4 h-4 text-surface-600 dark:text-surface-400" />
          </button>
          
          <pre className="bg-surface-100 dark:bg-surface-800 rounded-lg p-4 text-sm font-mono text-surface-700 dark:text-surface-300 overflow-x-auto">
            {activeTab === 'html' && getHTMLSnippet(getMaskedApiKey(), filters, sdkInfo?.urls?.widget, sdkInfo?.urls?.apiUrl, sdkConfig.widgetConfig?.displayMode)}
            {activeTab === 'react' && getReactSnippet(getMaskedApiKey(), filters, sdkInfo?.urls?.widget, sdkInfo?.urls?.apiUrl, sdkConfig.widgetConfig?.displayMode)}
            {activeTab === 'vue' && getVueSnippet(getMaskedApiKey(), filters, sdkInfo?.urls?.widget, sdkInfo?.urls?.apiUrl, sdkConfig.widgetConfig?.displayMode)}
          </pre>
        </div>
        
        {/* Installation Notes */}
        <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <h4 className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-2">
            {activeTab === 'html' && 'HTML Integration Notes'}
            {activeTab === 'react' && 'React Integration Notes'}
            {activeTab === 'vue' && 'Vue Integration Notes'}
          </h4>
          <ul className="text-xs text-amber-700 dark:text-amber-300 space-y-1 list-disc list-inside">
            {activeTab === 'html' && sdkConfig.widgetConfig?.displayMode === 'embedded' && (
              <>
                <li>Create a container element with a unique ID where the chat will be embedded</li>
                <li>Set the container dimensions (height is required for proper display)</li>
                <li>The chat interface will be immediately visible inside the container</li>
                <li>Replace filter values with actual values from your application</li>
              </>
            )}
            {activeTab === 'html' && sdkConfig.widgetConfig?.displayMode !== 'embedded' && (
              <>
                <li>Add the script tag before your closing {`</body>`} tag</li>
                <li>Replace filter values with actual values from your application</li>
                <li>The widget will appear as a floating chat button</li>
              </>
            )}
            {activeTab === 'react' && sdkConfig.widgetConfig?.displayMode === 'embedded' && (
              <>
                <li>No npm package required - the widget is loaded dynamically via script tag</li>
                <li>The component renders a container div where the chat will be embedded</li>
                <li>Pass height and width props to customize the chat dimensions</li>
                <li>Store your API key in environment variables (REACT_APP_NEOSHIFT_API_KEY)</li>
              </>
            )}
            {activeTab === 'react' && sdkConfig.widgetConfig?.displayMode !== 'embedded' && (
              <>
                <li>No npm package required - the widget is loaded dynamically via script tag</li>
                <li>Store your API key in environment variables (REACT_APP_NEOSHIFT_API_KEY)</li>
                <li>Pass filter values from your application state/context or props</li>
                <li>The useEffect hook handles loading and cleanup automatically</li>
              </>
            )}
            {activeTab === 'vue' && sdkConfig.widgetConfig?.displayMode === 'embedded' && (
              <>
                <li>No npm package required - the widget is loaded dynamically via script tag</li>
                <li>The component renders a container div where the chat will be embedded</li>
                <li>Pass height and width props to customize the chat dimensions</li>
                <li>Store your API key in environment variables (VITE_NEOSHIFT_API_KEY)</li>
              </>
            )}
            {activeTab === 'vue' && sdkConfig.widgetConfig?.displayMode !== 'embedded' && (
              <>
                <li>No npm package required - the widget is loaded dynamically via script tag</li>
                <li>Store your API key in environment variables (VITE_NEOSHIFT_API_KEY)</li>
                <li>Use computed properties for reactive filter values</li>
                <li>The onMounted/onBeforeUnmount hooks handle loading and cleanup</li>
              </>
            )}
          </ul>
        </div>
      </div>
      
      {/* Testing Section */}
      <div className="card">
        <h3 className="text-lg font-display font-semibold text-surface-900 dark:text-white mb-4">
          <BeakerIcon className="w-5 h-5 inline mr-2" />
          Test Configuration
        </h3>
        <p className="text-sm text-surface-500 dark:text-surface-400 mb-4">
          Enter sample filter values to test your SDK configuration.
        </p>
        
        {filters.length > 0 ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {filters.map((filter) => (
                <div key={filter.fieldName}>
                  <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                    {filter.displayName}
                    {filter.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <input
                    type={filter.dataType === 'number' ? 'number' : 'text'}
                    value={testFilters[filter.fieldName] || ''}
                    onChange={(e) => setTestFilters(prev => ({
                      ...prev,
                      [filter.fieldName]: e.target.value
                    }))}
                    placeholder={`Enter ${filter.displayName.toLowerCase()}`}
                    className="input"
                  />
                </div>
              ))}
            </div>
            
            <button
              onClick={handleTest}
              disabled={testing}
              className="btn-primary"
            >
              {testing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  Testing...
                </>
              ) : (
                <>
                  <BeakerIcon className="w-4 h-4 mr-2" />
                  Test Configuration
                </>
              )}
            </button>
            
            {testResult && (
              <div className={`mt-4 p-4 rounded-lg ${
                testResult.error 
                  ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                  : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
              }`}>
                {testResult.error ? (
                  <p className="text-sm text-red-600 dark:text-red-400">{testResult.error}</p>
                ) : (
                  <div>
                    <p className="text-sm text-green-600 dark:text-green-400 font-medium mb-2">
                      ✓ Configuration is valid!
                    </p>
                    {testResult.preview?.interpolatedPrompt && (
                      <div className="mt-2">
                        <p className="text-xs text-surface-500 mb-1">Interpolated Prompt Preview:</p>
                        <pre className="text-xs bg-white dark:bg-surface-800 p-2 rounded overflow-x-auto">
                          {testResult.preview.interpolatedPrompt}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-surface-400 italic">
            No filter fields configured. Contact your administrator to set up filters.
          </p>
        )}
      </div>
    </div>
  );
}
